/**
 * One row of the reservations table. Extracted from `ReservationsPanel`
 * (BON-472) along with its siblings, so that file declares one component.
 */
import type { ReservationRow } from '../../api/reservations';

import { expiresInLabel, formatMoney, formatWorkshopWhen } from './reservationFormat';
import { ReservationRowActions, type ReservationActionHandlers } from './ReservationRowActions';
import { ReservationSendFailedNote } from './ReservationSendFailedNote';
import { ReservationStagePill } from './ReservationStagePill';

export function ReservationTableRow({
  row,
  canWrite,
  ...handlers
}: { row: ReservationRow; canWrite: boolean } & ReservationActionHandlers) {
  return (
    <tr className="border-t border-stone-200 dark:border-stone-700">
      <td className="px-4 py-3 font-mono text-xs text-stone-500">{row.reference}</td>
      <td className="px-4 py-3">
        <div className="text-sm font-semibold text-bark-900 dark:text-stone-100">
          {row.customerName ?? '—'}
        </div>
        <div className="text-xs text-stone-500">{row.customerEmail}</div>
        {row.linkSendFailed && <ReservationSendFailedNote stage={row.stage} />}
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-bark-900 dark:text-stone-100">{row.workshopName ?? '—'}</div>
        <div className="text-xs text-stone-500">
          {formatWorkshopWhen(row.workshopStartsAt)}
          {row.venueName ? ` · ${row.venueName}` : ''}
        </div>
      </td>
      <td className="px-4 py-3 text-center text-sm">{row.seats}</td>
      <td className="px-4 py-3">
        <ReservationStagePill stage={row.stage} />
        <div className="mt-0.5 text-xs text-stone-500">{row.reservedBy?.name ?? '—'}</div>
      </td>
      <td className="px-4 py-3 text-sm text-stone-600 dark:text-stone-300">
        {expiresInLabel(row.expiresAt)}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="text-sm text-bark-900 dark:text-stone-100">
          {formatMoney(row.totalCents, row.currency)}
        </div>
        <div className="text-xs text-stone-500">unpaid</div>
      </td>
      {/* The whole column goes when there is nothing to put in it. A read-only
          viewer used to get an Actions heading over a strip of empty cells. */}
      {canWrite && (
        <td className="px-4 py-3 text-right">
          <ReservationRowActions row={row} {...handlers} />
        </td>
      )}
    </tr>
  );
}
